import { useMemo, useState } from 'react';
import { Plus, X, Search, BookmarkPlus } from 'lucide-react';
import { toast } from 'sonner';
import { crmStore, useCrm } from '../store';
import type { FactureLigne, Produit } from '../types';
import { TvaSelect } from './TvaSelect';
import { prixNetHT, prixSaisiVersHT, htToTtc, estChiffree, round2 } from '../utils/finance';
import { formatEUR } from '../utils';

const inputCls = 'w-full bg-slate-950/60 border border-slate-700/50 rounded-md px-2 py-1.5 text-xs text-slate-200 placeholder:text-slate-600';

const TYPES: { v: string; l: string }[] = [
  { v: 'article', l: 'Article' },
  { v: 'section', l: 'Titre / section' },
  { v: 'commentaire', l: 'Commentaire' },
  { v: 'saut_page', l: 'Saut de page' },
];

/** Éditeur de lignes détaillées : référence, unité, saisie HT/TTC, remise ligne, sections, catalogue. */
export function LignesFactureEditor({ lignes, onChange, readOnly = false }: {
  lignes: FactureLigne[]; onChange: (l: FactureLigne[]) => void; readOnly?: boolean;
}) {
  const data = useCrm();
  const catalogue = ((data.produits || []) as Produit[]).filter((p) => p.actif !== false);
  const [base, setBase] = useState<'HT' | 'TTC'>('HT');
  const [q, setQ] = useState('');

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return catalogue.filter((p) => `${p.reference || ''} ${p.libelle} ${p.categorie} ${p.sousFamille || ''} ${p.description}`.toLowerCase().includes(t)).slice(0, 10);
  }, [catalogue, q]);

  const upd = (i: number, patch: Partial<FactureLigne>) => onChange(lignes.map((l, idx) => {
    if (idx !== i) return l;
    const n = { ...l, ...patch };
    const brut = n.prixBrutHT ?? n.prixUnitaireHT;
    n.prixUnitaireHT = prixNetHT(brut, n.remisePct || 0);
    return n;
  }));
  const add = (l: Partial<FactureLigne> = {}) => onChange([...lignes, { description: '', quantite: 1, prixUnitaireHT: 0, prixBrutHT: 0, tvaPct: 20, remisePct: 0, typeLigne: 'article', ...l }]);
  const remove = (i: number) => onChange(lignes.filter((_, idx) => idx !== i));
  const setType = (i: number, t: string) => upd(i, estChiffree(t) ? { typeLigne: t, quantite: 1 } : { typeLigne: t, quantite: 0, prixBrutHT: 0, prixUnitaireHT: 0, remisePct: 0 });

  const addProduit = (p: Produit) => {
    add({
      reference: p.reference || null, unite: p.unite || null,
      description: [p.libelle, p.description].filter(Boolean).join('\n'),
      prixBrutHT: p.prixHT, prixUnitaireHT: p.prixHT, tvaPct: p.tvaPct ?? 20,
    });
    setQ('');
  };

  const saveToCatalogue = async (l: FactureLigne) => {
    const [libelle, ...rest] = (l.description || '').split('\n');
    if (!libelle.trim()) { toast.error('Saisissez d’abord une désignation'); return; }
    try {
      await crmStore.saveProduit({ categorie: '', libelle, description: rest.join('\n'), coutHT: 0, prixHT: l.prixBrutHT ?? l.prixUnitaireHT, unite: l.unite || '', reference: l.reference || '', tvaPct: l.tvaPct });
      toast.success(`« ${libelle} » enregistré dans le catalogue`);
    } catch (e: any) { toast.error(e?.message || 'Enregistrement impossible'); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="text-[10px] uppercase tracking-widest text-slate-400">Lignes</h3>
        {!readOnly && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-md border border-slate-700 overflow-hidden text-[11px]" title="Saisir les prix en HT ou en TTC (conversion automatique)">
              {(['HT', 'TTC'] as const).map((b) => (
                <button key={b} type="button" onClick={() => setBase(b)} className={`px-2 py-1 ${base === b ? 'bg-amber-500 text-slate-950' : 'text-slate-400'}`}>Saisie {b}</button>
              ))}
            </div>
            <div className="relative">
              <Search size={12} strokeWidth={1.5} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher au catalogue (ex : photo)" className={`${inputCls} pl-7 w-64`} />
              {results.length > 0 && (
                <div className="absolute right-0 z-30 mt-1 w-80 bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-1 max-h-72 overflow-auto">
                  {results.map((p) => (
                    <button key={p.id} type="button" onClick={() => addProduit(p)} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-800">
                      <div className="text-xs text-slate-200 truncate">{p.reference && <span className="font-mono text-teal-300 mr-1">{p.reference}</span>}{p.libelle}</div>
                      <div className="text-[10px] text-slate-500">{p.categorie || '—'} · {formatEUR(p.prixHT)} HT</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="button" onClick={() => add()} className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300"><Plus size={12} strokeWidth={1.5} /> Ligne</button>
            <button type="button" onClick={() => add({ typeLigne: 'section', quantite: 0, prixBrutHT: 0 })} className="text-xs text-slate-400 hover:text-slate-200">+ Section</button>
          </div>
        )}
      </div>
      <div className="space-y-2">
        {lignes.map((l, i) => {
          const chiffree = estChiffree(l.typeLigne);
          const brut = l.prixBrutHT ?? l.prixUnitaireHT;
          const affiche = base === 'TTC' ? htToTtc(brut, l.tvaPct) : brut;
          return (
            <div key={i} className={`grid grid-cols-12 gap-2 items-start rounded-md p-2 ${chiffree ? 'bg-slate-950/40' : 'bg-amber-500/5 border border-amber-500/20'}`}>
              <select disabled={readOnly} value={l.typeLigne || 'article'} onChange={(e) => setType(i, e.target.value)} className={`${inputCls} col-span-12 md:col-span-2`}>
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
              {chiffree && <input disabled={readOnly} value={l.reference || ''} onChange={(e) => upd(i, { reference: e.target.value })} placeholder="Réf." className={`${inputCls} col-span-3 md:col-span-1 font-mono`} />}
              <textarea disabled={readOnly} rows={l.typeLigne === 'saut_page' ? 1 : 2} value={l.description} onChange={(e) => upd(i, { description: e.target.value })}
                placeholder={l.typeLigne === 'section' ? 'Titre de section' : l.typeLigne === 'saut_page' ? '(saut de page)' : l.typeLigne === 'commentaire' ? 'Commentaire' : 'Désignation'}
                className={`${inputCls} ${chiffree ? 'col-span-9 md:col-span-2' : 'col-span-11 md:col-span-9'}`} />
              {chiffree && (<>
                <input disabled={readOnly} type="number" step="0.01" value={l.quantite} onChange={(e) => upd(i, { quantite: parseFloat(e.target.value) || 0 })} title="Quantité" className={`${inputCls} col-span-2 md:col-span-1`} />
                <input disabled={readOnly} value={l.unite || ''} onChange={(e) => upd(i, { unite: e.target.value })} placeholder="Unité" className={`${inputCls} col-span-2 md:col-span-1`} />
                <input disabled={readOnly} type="number" step="0.01" value={affiche} onChange={(e) => { const v = parseFloat(e.target.value) || 0; upd(i, { prixBrutHT: prixSaisiVersHT(v, base, l.tvaPct) }); }} title={`Prix unitaire ${base}`} className={`${inputCls} col-span-3 md:col-span-1`} />
                <input disabled={readOnly} type="number" step="0.1" value={l.remisePct || 0} onChange={(e) => upd(i, { remisePct: parseFloat(e.target.value) || 0 })} title="Remise ligne (%)" className={`${inputCls} col-span-2 md:col-span-1`} />
                <TvaSelect value={l.tvaPct} onChange={(v) => upd(i, { tvaPct: v })} className={`${inputCls} col-span-2 md:col-span-1`} />
                <div className="col-span-8 md:col-span-1 text-right text-xs text-slate-200 tabular-nums self-center">{formatEUR(round2(l.quantite * l.prixUnitaireHT))}</div>
              </>)}
              {!readOnly && (
                <div className="col-span-1 flex flex-col items-center gap-1 self-center">
                  <button type="button" onClick={() => remove(i)} className="text-red-400 hover:text-red-300" title="Supprimer"><X size={14} strokeWidth={1.5} /></button>
                  {chiffree && !l.reference && <button type="button" onClick={() => saveToCatalogue(l)} className="text-teal-400 hover:text-teal-300" title="Enregistrer dans le catalogue ?"><BookmarkPlus size={13} strokeWidth={1.5} /></button>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-slate-500 mt-1">Colonnes : type · réf. · désignation · qté · unité · prix unitaire {base} · remise % · TVA · total HT net</div>
    </div>
  );
}
